import { v4 } from 'uuid';

import { createFilterReducer } from '../filter';
import { eventsFilterActions, eventsFilterActionsPrefix } from '../../constants/filter';
import { aggregationTypes, dateColumns, usageEvents } from '../../constants/event';
import { getDefaultGroupColumn, isEventSpecificField } from '../../utils/event/common';
import { excludeEventSpecificClauses } from '../../utils/filter';
import { fromFilter } from '../../converters/filterConverter';
import { EVENT_TEMPLATE_TYPE, TEMPLATE_AGGREGATION_TYPE } from '../../constants/dashboards';
import { PARAM_CSV } from '../../utils/csv';
import periodReducer, { initialPeriod } from './periods';
import { sortingOrder, sortingTypes } from '../../constants/common';

const filterReducer = createFilterReducer(eventsFilterActionsPrefix);

export const alphabet = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ';

const createNewEvent = (letterNumber) => ({
  id: v4(),
  uniqueChar: alphabet[letterNumber],
  name: null,
  filter: filterReducer(undefined, {}),
  aggregationColumn: null,
  aggregationType: aggregationTypes.COUNT,
  splitColumn: null,
  othersFilter: null,
  sql: '',
  error: '',
  periodsState: {
    periods: [initialPeriod],
    isShow: false,
  },
  eventSort: {
    sortOrder: sortingOrder.ASCENDING,
    sortType: sortingTypes.NAME,
  },
});

const createDuplicateEvent = (duplicatedEvent) => ({
  ...duplicatedEvent,
  id: v4(),
  filter: {
    ...duplicatedEvent.filter,
    clauses: duplicatedEvent.filter.clauses.map((clause) => ({
      ...clause,
      id: v4(),
    })),
  },
});

const csvInitial = {
  [PARAM_CSV.HEADER]: true,

  [PARAM_CSV.GROUP_BY]: false,
  [PARAM_CSV.GROUP_BY_DAY]: true,
  [PARAM_CSV.GROUP_BY_MS]: false,

  [PARAM_CSV.SPLIT_BY]: false,
  [PARAM_CSV.SPLIT_BY_DAY]: false,
  [PARAM_CSV.SPLIT_BY_MS]: false,

  [PARAM_CSV.COUNT]: true,

  [PARAM_CSV.SUM]: false,
  [PARAM_CSV.AVG_SUM]: false,

  [PARAM_CSV.MAX]: false,
  [PARAM_CSV.MIN]: false,
  [PARAM_CSV.AVG]: false,
  [PARAM_CSV.PERIODS]: true,
};

const initialState = {
  events: [createNewEvent(0)],
  groupColumn: dateColumns.DAY,
  idUsingTemplate: undefined,
  csv: csvInitial,
};

const isEmptyOrSpecificField = (field) => !field || isEventSpecificField(field);

const isSplitAggregationType = (aggregationType) => {
  return (
    aggregationType === aggregationTypes.AVG_COUNT_SPLIT ||
    aggregationType === aggregationTypes.AVG_TOTAL_SPLIT
  );
};

const getEventAggregationType = (aggregationType, splitColumn) => {
  if (!isSplitAggregationType(aggregationType)) {
    return aggregationType;
  }
  if (isEmptyOrSpecificField(splitColumn)) {
    return aggregationTypes.COUNT;
  }
  return aggregationType;
};

const idPredicateBuilder = (id) => (e) => e.id === id;

const updateEvents = (collection, predicate, updateCallback) =>
  collection.map((item, i) => (predicate(item, i) ? updateCallback(item) : item));

const updateEventById = (collection, id, updateCallback) =>
  updateEvents(collection, idPredicateBuilder(id), updateCallback);

const moveCharLeft = (events, excludeId) => {
  let isMoveUniqueChar = false;

  return events.map((event) => {
    const eventVar = event;

    if (eventVar.id === excludeId) {
      isMoveUniqueChar = true;
    }
    if (isMoveUniqueChar) {
      const index = alphabet.indexOf(eventVar.uniqueChar);
      eventVar.uniqueChar = alphabet[index - 1];
    }
    return eventVar;
  });
};

const moveCharRight = (events, excludeIndex) => {
  let isMoveUniqueChar = false;
  return events.map((event, i) => {
    const eventVar = event;

    if (i === excludeIndex) {
      isMoveUniqueChar = true;
    }
    if (isMoveUniqueChar) {
      eventVar.uniqueChar = alphabet[i];
    }
    return eventVar;
  });
};

const updateGroupColumn = (groupColumn, events) => {
  const eventNames = events.map((e) => e.name);
  const uniqEventNames = Array.from(new Set(eventNames));

  const selectedUsageEvents = uniqEventNames.filter((name) => !!usageEvents[name]);

  if (
    groupColumn &&
    isEventSpecificField(groupColumn) &&
    (uniqEventNames.length > 1 || eventNames.length === 1) &&
    !selectedUsageEvents[0]
  ) {
    return null;
  }

  return selectedUsageEvents.length > 0
    ? getDefaultGroupColumn(selectedUsageEvents[0])
    : groupColumn;
};

const getEventsFromTemplate = (template) =>
  template.queries.map((item, index) => {
    return {
      ...createNewEvent(index),
      name: item.query.eventName,
      aggregationColumn:
        item.query.aggregations[0].field === undefined ? null : item.query.aggregations[0].field,
      aggregationType: TEMPLATE_AGGREGATION_TYPE[item.query.aggregations[0].type],
      filter: fromFilter(item.query.filter, (field) =>
        template.filterExtractor(item.query.eventName, field)
      ),
      splitColumn: item.query.groupBy[1] === undefined ? null : item.query.groupBy[1],
    };
  });

const compose = (state = initialState, action) => {
  switch (action.type) {
    case 'EVENTS_COMPOSE_ADD_EVENT':
      return state.events.length < alphabet.length
        ? {
            ...state,
            events: [...state.events, createNewEvent(state.events.length)],
          }
        : state;

    case 'EVENTS_COMPOSE_DUPLICATE_EVENT': {
      const eventsArr = [...state.events];
      const indexOfDuplicatedEvent = eventsArr
        .map((event) => event.id)
        .reduce((prevValue, id, index) => {
          const prevValueVar = prevValue;
          prevValueVar[id] = index;

          return prevValueVar;
        }, {})[action.id];
      const dplEvent = eventsArr[indexOfDuplicatedEvent];
      const indexOfOthersFilter = dplEvent.filter?.clauses?.findIndex(
        ({ id }) => id === dplEvent.othersFilter
      );
      const duplicateEvent = createDuplicateEvent(dplEvent);
      duplicateEvent.othersFilter =
        indexOfOthersFilter >= 0 ? duplicateEvent.filter?.clauses[indexOfOthersFilter]?.id : null;

      eventsArr.splice(indexOfDuplicatedEvent + 1, 0, duplicateEvent);

      return state.events.length < alphabet.length
        ? {
            ...state,
            events: moveCharRight(eventsArr, indexOfDuplicatedEvent + 1),
          }
        : state;
    }

    case 'EVENTS_COMPOSE_REMOVE_EVENT':
      return {
        ...state,
        events: moveCharLeft(state.events, action.id).filter((event) => event.id !== action.id),
      };

    case 'EVENTS_COMPOSE_SET_GROUP_COLUMN':
      return {
        ...state,
        groupColumn: action.groupColumn,
      };

    case 'EVENTS_COMPOSE_SET_EVENT_NAME': {
      const idPredicate = idPredicateBuilder(action.id);

      const predicate = usageEvents[action.name]
        ? (e) => idPredicate(e) || usageEvents[e.name]
        : idPredicate;

      const getAggregationColumn = ({ aggregationType, splitColumn, aggregationColumn }) =>
        (getEventAggregationType(aggregationType, splitColumn) === aggregationTypes.COUNT &&
          aggregationType === aggregationTypes.AVG_TOTAL_SPLIT) ||
        isEmptyOrSpecificField(aggregationColumn)
          ? null
          : aggregationColumn;

      const updatedEvents = updateEvents(state.events, predicate, (event) => {
        const filter = excludeEventSpecificClauses(
          event.filter,
          event.name || '',
          action.name,
          action.eventMetainf
        );
        return {
          ...event,
          name: action.name,
          filter,
          aggregationColumn: getAggregationColumn(event),
          splitColumn: isEmptyOrSpecificField(event.splitColumn) ? null : event.splitColumn,
          aggregationType: getEventAggregationType(event.aggregationType, event.splitColumn),
          othersFilter: filter.clauses?.find(({ id }) => id === event.othersFilter)
            ? event.othersFilter
            : null,
        };
      });

      return {
        ...state,
        events: updatedEvents,
        groupColumn: updateGroupColumn(state.groupColumn, updatedEvents),
      };
    }

    case 'EVENTS_COMPOSE_SET_EVENT_AGGREGATION_TYPE':
      return {
        ...state,
        events: updateEventById(state.events, action.id, (event) => ({
          ...event,
          aggregationColumn: null,
          aggregationType: action.aggregationType,
        })),
      };

    case 'EVENTS_COMPOSE_SET_EVENT_AGGREGATION_COLUMN':
      return {
        ...state,
        events: updateEventById(state.events, action.id, (event) => ({
          ...event,
          aggregationColumn: action.aggregationColumn,
        })),
      };

    case 'EVENTS_COMPOSE_SET_EVENT_SPLIT_COLUMN':
      return {
        ...state,
        events: updateEventById(state.events, action.id, (event) => ({
          ...event,
          splitColumn: action.splitColumn,
        })),
      };

    case 'SET_OTHERS_FILTER':
      return {
        ...state,
        events: updateEventById(state.events, action.eventId, (event) => ({
          ...event,
          othersFilter: action.clauseId,
        })),
      };

    case 'EXPOSE_EVENT_QUERY_SUCCESS':
      return {
        ...state,
        events: updateEventById(state.events, action.id, (event) => ({
          ...event,
          error: '',
          sql: action.sql,
        })),
      };

    case 'SET_EVENT_EXPOSURE_ERROR':
      return {
        ...state,
        events: updateEventById(state.events, action.id, (event) => ({
          ...event,
          sql: '',
          error: action.error,
        })),
      };

    case eventsFilterActions.ADD_FILTER_CLAUSE:
    case eventsFilterActions.COPY_FILTER:
    case eventsFilterActions.REMOVE_FILTER:
    case eventsFilterActions.SET_FILTER_CLAUSE_FIELD:
    case eventsFilterActions.SET_FILTER_CLAUSE_VALUE:
    case eventsFilterActions.SET_CLAUSES_CONCAT_OPERATOR:
    case eventsFilterActions.SET_FILTER_CLAUSE_OPERATOR:
    case eventsFilterActions.SET_FILTER_VISIBILITY:
      return {
        ...state,
        events: updateEventById(state.events, action.eventId, (event) => ({
          ...event,
          filter: filterReducer(event.filter, action),
        })),
      };

    case eventsFilterActions.REMOVE_FILTER_CLAUSE:
      return {
        ...state,
        events: updateEventById(state.events, action.eventId, (event) => ({
          ...event,
          filter: filterReducer(event.filter, action),
          othersFilter: event.othersFilter !== action.id ? event.othersFilter : null,
        })),
      };

    case 'ADD_PERIOD':
    case 'DELETE_PERIOD':
    case 'SET_PERIOD':
    case 'COPY_PERIODS':
      return {
        ...state,
        events: updateEventById(state.events, action.eventId, (event) => ({
          ...event,
          periodsState: periodReducer(event.periodsState, action),
          othersFilter: event.othersFilter,
        })),
      };

    case 'SET_SHOW_PERIODS':
      return {
        ...state,
        events: updateEventById(state.events, action.eventId, (event) => ({
          ...event,
          periodsState: periodReducer(event.periodsState, action),
          othersFilter: action.value === true ? null : event.othersFilter,
        })),
      };

    case `SET_TEMPLATE_${EVENT_TEMPLATE_TYPE}`:
      return {
        ...initialState,
        events: getEventsFromTemplate(action.template),
        groupColumn: action.template.queries[0].query.groupBy[0],
        idUsingTemplate: action.template.id,
      };

    case `CREATE_TEMPLATE_${EVENT_TEMPLATE_TYPE}_SUCCESS`:
      return {
        ...state,
        idUsingTemplate: action.template.id,
      };

    case 'DELETE_TEMPLATE_SUCCESS':
      return {
        ...state,
        idUsingTemplate: state.idUsingTemplate === action.id ? undefined : state.idUsingTemplate,
      };

    case `RESET_TEMPLATE_${EVENT_TEMPLATE_TYPE}`:
      return {
        ...state,
        idUsingTemplate: undefined,
      };

    case 'RESET_COMPOSE_EVENT':
      return initialState;

    case 'SET_EVENT_CSV_COLUMN':
      return {
        ...state,
        csv: {
          ...state.csv,
          ...action.csv,
        },
      };

    default:
      return state;
  }
};

const getEvent = (state, id) => (state.events || []).find((e) => e.id === id) || {};

export default compose;

export const getIds = (state) => state.events.map((e) => e.id);

export const getGroupColumn = (state) => state.groupColumn;

export const getSql = (state, id) => getEvent(state, id).sql;

export const getError = (state, id) => getEvent(state, id).error;

export const getName = (state, id) => getEvent(state, id).name;

export const getUniqueChar = (state, id) => getEvent(state, id).uniqueChar;

export const getPeriodsState = (state, id) => getEvent(state, id).periodsState;

export const getFilter = (state, id) => getEvent(state, id).filter;

export const getAggregationColumn = (state, id) => getEvent(state, id).aggregationColumn;

export const getAggregationType = (state, id) => getEvent(state, id).aggregationType;

export const getSplitColumn = (state, id) => getEvent(state, id).splitColumn;

export const getEvents = (state) => state.events;

export const getIdUsingTemplate = (state) => state.idUsingTemplate;

export const getCsv = (state) => state.csv;

export const getOthersFilter = (state, id) => getEvent(state, id).othersFilter;
